import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { SearchBar } from "@/components/SearchBar";
import { Spotlight } from "@/components/core/spotlight";
import { CORPORATE_EVENT_FORMATS } from "@/data/corporate-event-formats";
import { cn } from "@/lib/utils";

type HeroSlide = {
  word: string;
  label: string;
  title: string;
  description: string;
  imageUrl: string;
  alt: string;
};

// ids estaveis (nao traduzidos) usados como chave de lookup no JSON de traducao
const HERO_SLIDE_IMAGES = CORPORATE_EVENT_FORMATS.map(({ id }) => ({
  id,
  imageUrl: `/images/hero/corporate/${id}.webp`,
}));

function useHeroSlides(): HeroSlide[] {
  const { t } = useTranslation("landingHome");

  return HERO_SLIDE_IMAGES.map(({ id, imageUrl }) => ({
    word: t(`hero.slides.${id}.word`),
    label: t(`hero.slides.${id}.label`),
    title: t(`hero.slides.${id}.title`),
    description: t(`hero.slides.${id}.description`),
    alt: t(`hero.slides.${id}.alt`),
    imageUrl,
  }));
}

function useMobileQuery() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(media.matches);

    update();
    media.addEventListener("change", update);

    return () => media.removeEventListener("change", update);
  }, []);

  return isMobile;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);

    update();
    media.addEventListener("change", update);

    return () => media.removeEventListener("change", update);
  }, []);

  return prefersReducedMotion;
}

function useHeroCarousel(slides: HeroSlide[]) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!carouselApi) return;

    const handleSelect = () => setActiveIndex(carouselApi.selectedScrollSnap());

    handleSelect();
    carouselApi.on("select", handleSelect);
    carouselApi.on("reInit", handleSelect);

    return () => {
      carouselApi.off("select", handleSelect);
      carouselApi.off("reInit", handleSelect);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (prefersReducedMotion || slides.length < 2) return;

    const interval = window.setInterval(() => {
      if (!document.hidden) {
        if (carouselApi) {
          if (carouselApi.canScrollNext()) {
            carouselApi.scrollNext();
          } else {
            carouselApi.scrollTo(0);
          }
        } else {
          setActiveIndex((current) => (current + 1) % slides.length);
        }
      }
    }, 3800);

    return () => window.clearInterval(interval);
  }, [carouselApi, prefersReducedMotion, slides.length]);

  const goToSlide = (index: number) => {
    if (carouselApi) {
      carouselApi.scrollTo(index);
      return;
    }

    setActiveIndex(index);
  };

  return {
    carouselApi,
    setCarouselApi,
    activeIndex,
    activeSlide: slides[activeIndex] ?? slides[0],
    goToSlide,
  };
}

function MobileHeroCard({ slide, index }: { slide: HeroSlide; index: number }) {
  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted">
      <img
        src={slide.imageUrl}
        alt={slide.alt}
        className="h-full w-full object-cover"
        width={600}
        height={450}
        loading={index === 0 ? "eager" : "lazy"}
        {...{ fetchpriority: index === 0 ? "high" : "auto" }}
        decoding="async"
      />
    </div>
  );
}

function MobileHero() {
  const { t } = useTranslation("landingHome");
  const slides = useHeroSlides();
  const { setCarouselApi, activeIndex, activeSlide, goToSlide } = useHeroCarousel(slides);

  return (
    <section className="bg-background md:hidden">
      <div className="container px-3 pb-6 pt-8 sm:pt-10">
        <div className="space-y-6">
          <div className="px-1 pb-2">
            <h1 className="font-display text-[clamp(2rem,5.8vw,2.75rem)] font-semibold leading-[1.15] tracking-[-0.035em] text-foreground">
              {t("hero.headline")}
              <span className="mt-1.5 block text-primary">
                <span
                  key={activeSlide.word}
                  className="inline-block animate-word-slide"
                >
                  {activeSlide.word}
                </span>
              </span>
            </h1>

            <div className="mt-7">
              <SearchBar
                variant="hero"
                placeholder={t("hero.searchPlaceholder")}
                className="[&_input]:rounded-2xl [&_input]:border-border/50 [&_input]:bg-secondary/50 [&_input]:text-base [&_input]:placeholder:text-muted-foreground"
              />
            </div>
          </div>

          <div className="pt-2">
            <Carousel opts={{ align: "start", loop: false }} setApi={setCarouselApi} className="w-full">
              <CarouselContent className="-ml-3">
                {slides.map((slide, index) => (
                  <CarouselItem key={slide.word} className="basis-[94%] pl-3">
                    <MobileHeroCard slide={slide} index={index} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            <div className="px-1 pt-4">
              <h2 className="font-display text-lg font-semibold leading-snug tracking-tight text-foreground">
                {activeSlide.title}
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {activeSlide.description}
              </p>
            </div>
            <div className="mt-1 flex items-center justify-center" role="group" aria-label={t("hero.slideNavigation")}>
              {slides.map((slide, index) => (
                <button
                  key={slide.word}
                  type="button"
                  aria-label={t("hero.goToSlide", { slide: slide.word })}
                  aria-current={index === activeIndex ? "true" : undefined}
                  onClick={() => goToSlide(index)}
                  className="flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <span className={cn(
                    "h-1.5 rounded-full transition-[width,background-color] duration-200 motion-reduce:transition-none",
                    index === activeIndex ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/40"
                  )} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DesktopHero() {
  const { t } = useTranslation("landingHome");
  const slides = useHeroSlides();
  const { setCarouselApi, activeIndex, activeSlide, goToSlide } = useHeroCarousel(slides);

  return (
    <section className="relative hidden min-h-[85vh] overflow-hidden bg-background md:flex">
      <Spotlight size={400} className="z-0" />
      <div className="container relative z-10 flex w-full items-center py-14 lg:py-20">
        <div className="grid w-full items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(380px,1fr)] lg:gap-16">
          <div className="mx-auto max-w-3xl space-y-6 text-center lg:mx-0 lg:text-left">
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl">
              {t("hero.headline")}
              <span className="mt-2 block text-primary">
                <span
                  key={activeSlide.word}
                  className="inline-block animate-word-slide"
                >
                  {activeSlide.word}
                </span>
              </span>
            </h1>

            <p
              className="mx-auto max-w-2xl text-lg text-muted-foreground lg:mx-0 lg:text-xl"
            >
              {t("hero.subtitle")}
            </p>

            <div
              className="mx-auto w-full max-w-2xl lg:mx-0"
            >
              <SearchBar variant="hero" placeholder={t("hero.searchPlaceholder")} />
            </div>

            <div
              className="flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start"
            >
              <Button variant="default" size="lg" asChild>
                <Link to="/eventos">
                  {t("hero.exploreEvents")} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/produtores">{t("hero.iAmProducer")}</Link>
              </Button>
            </div>
          </div>

          <div
            className="w-full"
          >
            {/* Carrossel estilo cards com preview lateral */}
            <div className="relative">
              <Carousel
                opts={{
                  align: "center",
                  loop: false,
                }}
                setApi={setCarouselApi}
                className="w-full"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {slides.map((slide, index) => (
                    <CarouselItem
                      key={slide.word}
                      className="pl-2 md:pl-4 basis-[85%] md:basis-[80%] lg:basis-[75%]"
                    >
                      <div
                        className={cn(
                          "relative overflow-hidden rounded-[1.5rem] border border-border/60 bg-card shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500",
                          index === activeIndex
                            ? "scale-100 opacity-100"
                            : "scale-95 opacity-60"
                        )}
                      >
                        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                          <img
                            src={slide.imageUrl}
                            alt={slide.alt}
                            className="h-full w-full object-cover transition-transform duration-700"
                            width={600}
                            height={450}
                            loading={index === 0 ? "eager" : "lazy"}
                            {...{ fetchpriority: index === 0 ? "high" : "auto" }}
                            decoding="async"
                          />
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {/* Indicadores de slide */}
              <div className="mt-6 flex items-center justify-center gap-2">
                {slides.map((slide, index) => (
                  <button
                    key={slide.word}
                    type="button"
                    aria-label={t("hero.goToSlide", { slide: slide.word })}
                    onClick={() => goToSlide(index)}
                    className={cn(
                      "h-2 rounded-full transition-all duration-300",
                      index === activeIndex ? "w-8 bg-primary" : "w-2 bg-border hover:bg-primary/50"
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function LandingHero() {
  const isMobile = useMobileQuery();

  return isMobile ? <MobileHero /> : <DesktopHero />;
}

